export type SharedResourceShareMode = "private" | "shared_to_users" | "public";

export type SharedResourceShareCapability = "view" | "reference" | "clone";

export type SharedResourceShareGrantRecord<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TGranteeId extends string | number = number,
> = {
  grantee_type: "user";
  grantee_id: TGranteeId;
  capabilities?: TCapability[];
};

export type SharedResourceShareFormUser<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
> = {
  user_id: number;
  username: string;
  full_name: string;
  phone?: string | null;
  organization_name?: string | null;
  organization_unit_names: string[];
  capabilities: TCapability[];
};

export type SharedResourceShareFormValues<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
> = {
  share_mode: SharedResourceShareMode;
  shared_users: SharedResourceShareFormUser<TCapability>[];
};

export type SharedResourceShareMutationPayload<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TGranteeId extends string | number = number,
> = {
  share_mode: SharedResourceShareMode;
  share_grants: SharedResourceShareGrantRecord<TCapability, TGranteeId>[];
};

export type SharedResourceShareReadableRecord<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
> = {
  share_mode: SharedResourceShareMode;
  share_grants: SharedResourceShareGrantRecord<TCapability, string | number>[];
};

export type SharedResourceShareFormAdapter<
  TRecord,
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TPayload = SharedResourceShareMutationPayload<TCapability>,
> = {
  toFormValues: (record: TRecord) => SharedResourceShareFormValues<TCapability>;
  normalizePayload: (values: SharedResourceShareFormValues<TCapability>) => TPayload;
};

export function toSharedResourceShareFormValues<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
>(
  record: SharedResourceShareReadableRecord<TCapability>,
): SharedResourceShareFormValues<TCapability> {
  return {
    share_mode: record.share_mode,
    shared_users: record.share_grants.map((item) => ({
      user_id: Number(item.grantee_id),
      username: "",
      full_name: "",
      organization_unit_names: [],
      capabilities: [...(item.capabilities ?? [])],
    })),
  };
}

export function normalizeSharedResourceShareFormPayload<
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
>(
  values: SharedResourceShareFormValues<TCapability>,
): SharedResourceShareMutationPayload<TCapability> {
  if (values.share_mode === "private") {
    return {
      share_mode: "private",
      share_grants: [],
    };
  }
  if (values.share_mode === "public") {
    return {
      share_mode: "public",
      share_grants: [],
    };
  }
  return {
    share_mode: "shared_to_users",
    share_grants: values.shared_users.map((item) => ({
      grantee_type: "user",
      grantee_id: item.user_id,
      capabilities: [...item.capabilities],
    })),
  };
}

export function createSharedResourceShareFormAdapter<
  TRecord extends SharedResourceShareReadableRecord<TCapability>,
  TCapability extends SharedResourceShareCapability = SharedResourceShareCapability,
  TGrantPayload = SharedResourceShareGrantRecord<TCapability>,
>(): SharedResourceShareFormAdapter<
  TRecord,
  TCapability,
  {
    share_mode: SharedResourceShareMode;
    share_grants: TGrantPayload[];
  }
> {
  return {
    toFormValues: (record) => toSharedResourceShareFormValues<TCapability>(record),
    normalizePayload: (values) => {
      const payload = normalizeSharedResourceShareFormPayload<TCapability>(values);
      return {
        share_mode: payload.share_mode,
        share_grants: payload.share_grants as TGrantPayload[],
      };
    },
  };
}
